'use client';
import React, { useState } from 'react';
import { Desktop } from '@components/Desktop';
import { Mobile } from '@components/Mobile';
import { ImageObj } from '@custom-types/ImageObj.types';
import Image from 'next/image';
import { Box, Stack } from '@mui/material';
import ParallaxText from '@components/ParallaxText';
import CustomCounter from '@components/CustomCounter/CustomCounter';
import cn from './style.module.scss';

type AmenitiesTab = {
  type: 'amenities';
  description: string;
  listElement: Array<{
    src: string;
    label: string;
  }>;
};

type LocationTab = {
  type: 'location';
  heading: string;
  description: string;
  listElement: Array<{
    title: string;
    number: string;
    unit: string;
  }>;
  images: ImageObj;
  mobImages: ImageObj;
};

interface SpecificationDataProps {
  tabs: Array<AmenitiesTab | LocationTab>;
}

type Props = {
  layoutClass?: string;
  t: any;
};
export default function TabData({
  tabs,
  layoutClass = '',
}: SpecificationDataProps & Props) {
  const [activeTab, setActiveTab] = useState(0);
  if (!tabs || tabs.length === 0) return null;

  const currentTab = tabs[activeTab];
  return (
    <Stack
      component="section"
      className={`${cn.beachHomeSpecData} ${cn[layoutClass]}`}
    >
      <Box
        className={`${cn.beachHomeDataInner} ${cn[`residences_${currentTab.type}`]}`}
      >
        <Box
          className={`${cn.beachHomeDataHeader} ${cn[`${currentTab.type}`]}`}
        >
          {currentTab.type === 'amenities' ? (
            <Box className={cn.beachHeaderDescription}>
              <ParallaxText
                variant="p"
                animationType="zoomIn"
                animationDuration={500}
              >
                {currentTab.description}
              </ParallaxText>
            </Box>
          ) : (
            <>
              <Box className={cn.beachHeaderTitle}>
                <ParallaxText
                  variant="h2"
                  animationType="zoomIn"
                  animationDuration={500}
                >
                  {currentTab.heading}
                </ParallaxText>
              </Box>
              <Box className={cn.beachHeaderDescription}>
                <ParallaxText
                  variant="p"
                  animationType="zoomIn"
                  animationDuration={500}
                >
                  {currentTab.description}
                </ParallaxText>
              </Box>
            </>
          )}
        </Box>
        <Box className={cn.beachHomeDataBody}>
          <Box className={cn.beachHomeDataContent}>
            <ul
              className={`${cn.beachHomeDataList} ${cn[`${currentTab.type}`]}`}
            >
              {currentTab.listElement?.map((item, idx) => {
                if (currentTab.type === 'amenities') {
                  const amenitiesItem = item as AmenitiesTab['listElement'][0];
                  return (
                    <li key={idx}>
                      <Box className={cn.amenitiesItem}>
                        <span className={cn.amenitiesItem__element}>
                          <Mobile>
                            <Image
                              src={amenitiesItem.src}
                              unoptimized
                              style={{ objectFit: 'contain' }}
                              alt={`Specification Data - Current Tab List Element ${idx + 1}`}
                              width={32}
                              height={32}
                            />
                          </Mobile>
                          <Desktop>
                            <Image
                              src={amenitiesItem.src}
                              unoptimized
                              style={{ objectFit: 'contain' }}
                              alt={`Specification Data - Current Tab List Element ${idx + 1}`}
                              width={52}
                              height={52}
                            />
                          </Desktop>
                        </span>
                        <ParallaxText
                          variant="p"
                          animationType="zoomIn"
                          animationDuration={500}
                        >
                          {amenitiesItem.label}
                        </ParallaxText>
                      </Box>
                    </li>
                  );
                } else {
                  const locationItem = item as LocationTab['listElement'][0];
                  return (
                    <li key={idx}>
                      <Box className={cn.locationItem}>
                        <Box className={cn.locationItem__title}>
                          <h4>{locationItem.title}</h4>
                        </Box>
                        <Box className={cn.locationItem__data}>
                          <CustomCounter
                            to={parseInt(locationItem.number) || 0}
                            tag="h2"
                            duration={2}
                          />
                          <h2>{locationItem.unit}</h2>
                        </Box>
                      </Box>
                    </li>
                  );
                }
              })}
            </ul>
          </Box>
          {currentTab.type === 'amenities' ? (
            <></>
          ) : (
            <>
              <Box className={cn.residencesBodyTheme}>
                <Mobile>
                  <Box className={cn.residencesThemeElement}>
                    <Image
                      width={500}
                      height={700}
                      unoptimized
                      quality={100}
                      src={currentTab.mobImages.url}
                      alt={currentTab.mobImages.alt}
                    />
                  </Box>
                </Mobile>
                <Desktop>
                  <Box className={cn.residencesThemeElement}>
                    <Image
                      width={500}
                      height={700}
                      unoptimized
                      quality={100}
                      src={currentTab.images.url}
                      alt={currentTab.images.alt}
                    />
                  </Box>
                </Desktop>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Stack>
  );
}
